import TiptapMention from '@tiptap/extension-mention'

export enum MentionEntities {
  Member = 'member',
  // A role or a circle, by its CIRCLE id: the same role may sit in several
  // circles, and a click must open the one that was meant (#92)
  Role = 'role',
}

export interface Mentionable {
  id: string
  name: string
  entity: MentionEntities
  // Told apart in the suggestion list only, e.g. the circle of a role whose
  // name another role shares (#92). Never written into the text.
  hint?: string
}

// People are mentioned with @, roles and circles with # (Yohan, 22/09/2026)
export function mentionChar(entity?: string): '@' | '#' {
  return entity === MentionEntities.Role ? '#' : '@'
}

// Markdown syntax: [@Jane Doe](rolebase://member/uuid),
// [#Coordination](rolebase://role/uuid)
const MENTION_MD_REGEX =
  /^\[[@#]([^\]]+)\]\(rolebase:\/\/([a-z]+)\/([a-zA-Z0-9-]+)\)/

export const Mention = TiptapMention.extend({
  // The character in front of the name comes from what is mentioned, not
  // from the key that was typed: a mention read back from Markdown has no
  // suggestion attached, and would otherwise always show « @ »
  addOptions() {
    return {
      // Always defined: this extends Tiptap's own Mention
      ...this.parent!(),
      renderText({ node }) {
        return `${mentionChar(node.attrs.entity)}${
          node.attrs.label ?? node.attrs.id
        }`
      },
      renderHTML({ options, node }) {
        return [
          'span',
          options.HTMLAttributes,
          `${mentionChar(node.attrs.entity)}${
            node.attrs.label ?? node.attrs.id
          }`,
        ]
      },
    }
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      entity: {
        default: MentionEntities.Member,
        parseHTML: (element) => element.getAttribute('data-entity'),
        renderHTML: (attributes) => ({ 'data-entity': attributes.entity }),
      },
    }
  },

  markdownTokenizer: {
    name: 'mention',
    level: 'inline',
    start: (src: string) => {
      const positions = [src.indexOf('[@'), src.indexOf('[#')].filter(
        (index) => index !== -1
      )
      return positions.length ? Math.min(...positions) : -1
    },
    tokenize(src: string) {
      const match = MENTION_MD_REGEX.exec(src)
      if (!match) return undefined
      return {
        type: 'mention',
        raw: match[0],
        label: match[1],
        entity: match[2],
        id: match[3],
      }
    },
  },

  parseMarkdown(token, helpers) {
    return helpers.createNode('mention', {
      id: token.id,
      label: token.label,
      entity: token.entity,
    })
  },

  renderMarkdown(node) {
    const { id, label, entity } = node.attrs ?? {}
    const kind = entity ?? MentionEntities.Member
    return `[${mentionChar(kind)}${label ?? id}](rolebase://${kind}/${id})`
  },
})
